import API from '@API/index';
import NonIdealState from '@components/common/NonIdealState/NonIdealState';
import { Search } from '@mui/icons-material';
import { Box, Card, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { IStoreState } from '@store/index';
import Utils from '@utils/index';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Storage } from 'aws-amplify';
import { isEmpty, isString } from 'lodash';
import { useSnackbar } from 'notistack';

const StyledCard = styled(Card)`
  :hover {
    cursor: pointer;
    box-shadow: 0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%);
  }
`

export type AITabProps = {
  navigateTimelineTo: (start: number) => void;
  timeframe: { start: Date, end: Date };
}

const AITab: React.FC<AITabProps> = (props) => {
  const { navigateTimelineTo, timeframe } = props;
  const { enqueueSnackbar } = useSnackbar();

  const selectedStream = useSelector((store: IStoreState) => store.selectedStream.stream);
  const selectedSite = useSelector((store: IStoreState) => store.selectedSites.selectedSite);
  const credentials = useSelector((store: IStoreState) => store.session.credentials)

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [events, setEvents] = React.useState<{ timestamp: number, pictureUrl?: string }[]>([]);

  const selectedStreamRef = React.useRef(selectedStream);
  const searchRef = React.useRef(search);
  const timezoneRef = React.useRef(selectedSite?.timezone)
  const credentialsRef = React.useRef(credentials);
  const enqueueSnackbarRef = React.useRef(enqueueSnackbar);
  const timeframeRef = React.useRef(timeframe);

  React.useEffect(() => { searchRef.current = search }, [search])
  React.useEffect(() => { selectedStreamRef.current = selectedStream }, [selectedStream]);
  React.useEffect(() => { timezoneRef.current = selectedSite?.timezone?.split(' ')[0] }, [selectedSite])
  React.useEffect(() => { credentialsRef.current = credentials }, [credentials]);
  React.useEffect(() => { enqueueSnackbarRef.current = enqueueSnackbar }, [enqueueSnackbar])
  React.useEffect(() => { timeframeRef.current = timeframe }, [timeframe])

  const processQuery = React.useCallback(async () => {
    if (selectedStreamRef.current) {
      try {
        setLoading(true);
        const data = await API.Events.fetchEventsWithLLMQuery(
          selectedStreamRef.current,
          searchRef.current,
          timeframeRef.current.end.getTime(),
          timeframeRef.current.start.getTime()
        );

        const getImage = async (element: { timestamp: number, s3_url?: string }, endpoint: any): Promise<{ timestamp: number, pictureUrl?: string }> => {
          try {
            const options = {
              credentials: credentialsRef.current,
              region: process.env.REACT_APP_KINESIS_VIDEO_STREAMS_REGION
            };

            if (isString(element.s3_url) && !isEmpty(element.s3_url)) {
              const url = await Storage.get(element.s3_url, {
                bucket: process.env.REACT_APP_S3_QUERY_IMAGES || "",
                contentDisposition: "attachment",
                expires: 86400
              }) as string

              return {
                timestamp: +element.timestamp,
                pictureUrl: url
              }
            }

            const images = await API.Kinesis.listStreamImages(
              selectedStreamRef.current?.kvsName || "",
              options,
              endpoint,
              new Date(element.timestamp),
              new Date(element.timestamp + 100)
            )

            console.log(images);
            const start = images.find(el => Boolean(el.ImageContent));
            return { timestamp: +element.timestamp, pictureUrl: start?.ImageContent ? `data:image/jpeg;base64,${start?.ImageContent}` : undefined }
          } catch (e) {
            console.log(e);
            return { timestamp: +element.timestamp }
          }
        }

        const options = {
          credentials: credentialsRef.current,
          region: process.env.REACT_APP_KINESIS_VIDEO_STREAMS_REGION
        };

        const endpoint = await API.Kinesis.getDataEndpoint(selectedStreamRef.current?.kvsName || "", options, 'GET_IMAGES');

        const eventsWithPictures = await Promise.all(data.data?.map(e => getImage(e, endpoint)) || [])
        setEvents(eventsWithPictures);
      } catch (e) {
        enqueueSnackbarRef.current(Utils.Error.getErrorMessage(e) || "There was an error processing your request.", { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }
  }, [])

  const generateEventTimestamp = React.useCallback((start: number) => {
    return `${Utils.Date.format(new Date(start), "MMM Do, YYYY h:mm:ss A", timezoneRef.current)}`
  }, [])

  return (
    <Box padding="1rem" width="100%" display="grid" gridTemplateRows="min-content min-content auto" gap="1rem">
      <Box width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Typography color="#191919" variant="subtitle1">EquipmentShare <span style={{ color: '#f16622' }} >AI</span> Beta</Typography>
      </Box>
      <Box>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Search"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          style={{ marginTop: 0 }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && search.length > 0) {
              processQuery()
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton disabled={loading || search.length === 0} size="small" onClick={() => processQuery()}>
                  <Search />
                </IconButton>
              </InputAdornment>
            )
          }}
          data-cy="header-search-input"
        />
      </Box>
      <Box width="100%" height="100%">
        {loading &&
          <Box width="100%" height="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        }
        {loading === false &&
          <Box maxHeight="76vh" overflow="auto" display="grid" gridAutoColumns="min-content" gap="1rem" padding="0.5rem">
            {events.map((event) => {
              return (
                <StyledCard onClick={() => navigateTimelineTo(event.timestamp)} elevation={3} variant="outlined" style={{ width: '23vw' }}>
                  <Box display="grid" gridTemplateRows="min-content auto" padding="0.5rem">
                    <Box display="flex" width="100%" flexDirection="row" alignItems="center" justifyItems="center">
                      <img style={{ height: '100%', width: '100%', objectFit: 'cover' }} src={event.pictureUrl} alt="" />
                    </Box>
                    <Box width="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                      <Typography variant="caption">
                        {generateEventTimestamp(event.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                </StyledCard>
              )
            })}
          </Box>
        }
        {loading === false && events.length === 0 &&
          <NonIdealState
            icon={<Search />}
            title={"AI Video Search"}
            description={"Ask questions in plain language to find events in your video stream."}
            padding="0 0"
          />
        }
      </Box>
    </Box>
  )
}

export default AITab;
